/**
 * Integrations Page
 * @description Tenant 관리자 - 연동 설정 페이지 (Connectors)
 */

import { useState, useEffect } from 'react';
import './tenant-admin.css';
import {
  Badge, Button, Card, Checkbox, Dialog, DialogContent, DialogDescription,
  DialogHeader, DialogTitle, DialogTrigger, Input, Label, Select, SelectContent,
  SelectItem, SelectTrigger, SelectValue
} from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import { dataService } from '@/services/dataService';
import {
    Settings,
    Plug,
    CheckCircle,
    AlertTriangle,
    Plus,
    Trash2,
    Power,
} from 'lucide-react';

interface Connector {
    connectorId: string;
    type: string;
    enabled: boolean;
    mode: string[];
}

interface ConnectorsData {
    schemaVersion: string;
    tenantId: string;
    generatedAt: string;
    connectors: Connector[];
}

// Connector 타입별 표시 정보
const connectorTypeInfo: Record<string, { name: string; description: string; category: string }> = {
    jira: { name: 'Jira', description: '이슈 트래킹 시스템 연동', category: 'Issue Tracking' },
    confluence: { name: 'Confluence', description: '문서 관리 및 협업 도구', category: 'Documentation' },
    servicenow_cmdb: { name: 'ServiceNow CMDB', description: 'IT 서비스 관리 플랫폼 CMDB 연동', category: 'ITSM' },
    oidc: { name: 'OIDC', description: 'OpenID Connect 기반 인증 제공자', category: 'Identity Provider' },
    datadog: { name: 'Datadog', description: '모니터링 및 관찰성 플랫폼', category: 'Observability' },
    prometheus: { name: 'Prometheus', description: '메트릭 수집 및 모니터링', category: 'Observability' },
    zabbix: { name: 'Zabbix', description: '인프라 모니터링 시스템', category: 'Observability' },
    aws_cloudwatch: { name: 'AWS CloudWatch', description: 'AWS 클라우드 모니터링 서비스', category: 'Observability' },
    github: { name: 'GitHub', description: '소스 코드 관리 및 협업 플랫폼', category: 'DevOps' },
    jenkins: { name: 'Jenkins', description: 'CI/CD 파이프라인 자동화 도구', category: 'DevOps' },
};

// 사용 가능한 connector 타입 목록
const availableConnectorTypes = [
    { value: 'jira', label: 'Jira' },
    { value: 'confluence', label: 'Confluence' },
    { value: 'servicenow_cmdb', label: 'ServiceNow CMDB' },
    { value: 'oidc', label: 'OIDC (Entra-ID, Okta 등)' },
    { value: 'datadog', label: 'Datadog' },
    { value: 'prometheus', label: 'Prometheus' },
    { value: 'zabbix', label: 'Zabbix' },
    { value: 'aws_cloudwatch', label: 'AWS CloudWatch' },
    { value: 'github', label: 'GitHub' },
    { value: 'jenkins', label: 'Jenkins' },
];

export const IntegrationsPage: React.FC = () => {
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [connectorsData, setConnectorsData] = useState<ConnectorsData | null>(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [selectedConnector, setSelectedConnector] = useState<Connector | null>(null);

    // Form states
    const [newConnectorForm, setNewConnectorForm] = useState({
        connectorId: '',
        type: '',
        enabled: false,
        mode: [] as string[],
    });

    const [connectorForm, setConnectorForm] = useState<Partial<Connector>>({});

    useEffect(() => {
        const currentUser = user;
        if (currentUser?.userType === 'tenant-user') {
            loadConnectors(currentUser.tenantId);
        }
    }, [user]);

    const loadConnectors = async (tenantId: string) => {
        try {
            setLoading(true);
            const data = await dataService.getTenantConnectors(tenantId);
            setConnectorsData(data);
        } catch (error) {
            console.error('연동 정보를 불러오는데 실패했습니다:', error);
            setConnectorsData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveConnectors = async (updatedData: ConnectorsData) => {
        const currentUser = user;
        if (currentUser?.userType !== 'tenant-user') return;

        try {
            await dataService.updateTenantConnectors(currentUser.tenantId, {
                connectors: updatedData.connectors,
            });
            setConnectorsData(updatedData);
            console.log('연동 데이터가 성공적으로 저장되었습니다.');
        } catch (error) {
            console.error('저장에 실패했습니다:', error);
            alert('저장에 실패했습니다. 다시 시도해주세요.');
        }
    };

    const handleAddConnector = () => {
        setNewConnectorForm({
            connectorId: '',
            type: '',
            enabled: false,
            mode: [],
        });
        setAddModalOpen(true);
    };

    const handleAddSubmit = () => {
        if (!connectorsData || !newConnectorForm.connectorId || !newConnectorForm.type) return;

        // connectorId 중복 확인
        if (connectorsData.connectors.some(c => c.connectorId === newConnectorForm.connectorId)) {
            alert('이미 존재하는 Connector ID입니다.');
            return;
        }

        const newConnector: Connector = {
            connectorId: newConnectorForm.connectorId,
            type: newConnectorForm.type,
            enabled: newConnectorForm.enabled,
            mode: newConnectorForm.mode,
        };

        const updatedConnectors = [...connectorsData.connectors, newConnector];
        handleSaveConnectors({
            ...connectorsData,
            connectors: updatedConnectors,
        });
        setAddModalOpen(false);
        setNewConnectorForm({ connectorId: '', type: '', enabled: false, mode: [] });
    };

    const handleConnectorSettings = (connector: Connector) => {
        setSelectedConnector(connector);
        setConnectorForm({
            connectorId: connector.connectorId,
            type: connector.type,
            enabled: connector.enabled,
            mode: [...connector.mode],
        });
        setSettingsModalOpen(true);
    };

    const handleSettingsSubmit = () => {
        if (!connectorsData || !selectedConnector) return;

        const updatedConnectors = connectorsData.connectors.map(c => {
            if (c.connectorId === selectedConnector.connectorId) {
                return {
                    ...c,
                    enabled: connectorForm.enabled !== undefined ? connectorForm.enabled : c.enabled,
                    mode: connectorForm.mode || c.mode,
                };
            }
            return c;
        });

        handleSaveConnectors({
            ...connectorsData,
            connectors: updatedConnectors,
        });
        setSettingsModalOpen(false);
        setSelectedConnector(null);
    };

    const handleToggleEnabled = (connector: Connector) => {
        if (!connectorsData) return;

        const updatedConnectors = connectorsData.connectors.map(c => {
            if (c.connectorId === connector.connectorId) {
                return { ...c, enabled: !c.enabled };
            }
            return c;
        });

        handleSaveConnectors({
            ...connectorsData,
            connectors: updatedConnectors,
        });
    };

    const handleDeleteConnector = (connectorId: string) => {
        if (!confirm(`정말로 연동 "${connectorId}"를 삭제하시겠습니까?`)) return;
        if (!connectorsData) return;

        const updatedConnectors = connectorsData.connectors.filter(c => c.connectorId !== connectorId);
        handleSaveConnectors({
            ...connectorsData,
            connectors: updatedConnectors,
        });
    };

    const getConnectorInfo = (type: string) => {
        return connectorTypeInfo[type] || { name: type, description: '연동 설정', category: '기타' };
    };

    const connectors = connectorsData?.connectors || [];
    const enabledCount = connectors.filter(c => c.enabled).length;
    const disabledCount = connectors.filter(c => !c.enabled).length;

    if (!user || user.userType !== 'tenant-user') {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-gray-500">테넌트 사용자로 로그인해주세요.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tenant-admin-page space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">연동 설정</h1>
                <p className="text-gray-600 mt-1">
                    외부 시스템과의 연동을 관리합니다.
                </p>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : connectorsData ? (
                <>
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <Plug className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">전체 연동</p>
                                    <p className="text-2xl font-bold text-gray-900">{connectors.length}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">활성화됨</p>
                                    <p className="text-2xl font-bold text-gray-900">{enabledCount}</p>
                                </div>
                            </div>
                        </Card>

                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-100 rounded-lg">
                                    <Power className="w-5 h-5 text-gray-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">비활성화됨</p>
                                    <p className="text-2xl font-bold text-gray-900">{disabledCount}</p>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Connectors List */}
                    <Card className="shadow-sm">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold">연동 목록</h2>
                                <Button onClick={handleAddConnector} className="flex items-center gap-2">
                                    <Plus className="h-4 w-4" />
                                    새 연동 추가
                                </Button>
                            </div>

                            <div className="space-y-3">
                                {connectors.map((connector) => {
                                    const info = getConnectorInfo(connector.type);
                                    return (
                                        <div
                                            key={connector.connectorId}
                                            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                                        >
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className={`p-2.5 rounded-lg border ${connector.enabled ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'}`}>
                                                    {connector.enabled ? (
                                                        <CheckCircle className="h-5 w-5 text-green-600" />
                                                    ) : (
                                                        <Power className="h-5 w-5 text-gray-400" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <h3 className="text-sm font-semibold text-gray-900">{info.name}</h3>
                                                        <Badge
                                                            variant={connector.enabled ? 'default' : 'secondary'}
                                                            className="text-xs px-2 py-1"
                                                        >
                                                            {connector.enabled ? '활성화' : '비활성화'}
                                                        </Badge>
                                                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                                                            {info.category}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mb-1">{connector.connectorId}</p>
                                                    <p className="text-xs text-gray-500 mb-1">{info.description}</p>
                                                    {connector.mode && connector.mode.length > 0 && (
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <span className="text-xs text-gray-400">모드:</span>
                                                            {connector.mode.map((m, idx) => (
                                                                <span key={idx} className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">
                                                                    {m}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-1.5 ml-4">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 px-3 hover:bg-blue-50 hover:border-blue-200"
                                                    onClick={() => handleToggleEnabled(connector)}
                                                >
                                                    <Power className="h-3.5 w-3.5 mr-1" />
                                                    {connector.enabled ? '비활성화' : '활성화'}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-200"
                                                    onClick={() => handleConnectorSettings(connector)}
                                                >
                                                    <Settings className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 hover:bg-red-50 hover:border-red-200"
                                                    onClick={() => handleDeleteConnector(connector.connectorId)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                                {connectors.length === 0 && (
                                    <div className="text-center py-12 text-gray-500">
                                        <Plug className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                                        <p className="text-sm">등록된 연동이 없습니다.</p>
                                        <p className="text-xs text-gray-400 mt-1">새 연동을 추가해보세요.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* 새 연동 추가 모달 */}
                    <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>새 연동 추가</DialogTitle>
                                <DialogDescription>
                                    새로운 외부 시스템 연동을 추가합니다.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="connectorId">Connector ID</Label>
                                    <Input
                                        id="connectorId"
                                        value={newConnectorForm.connectorId}
                                        onChange={(e) => setNewConnectorForm({ ...newConnectorForm, connectorId: e.target.value })}
                                        placeholder="예: jira-prod"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="type">연동 타입</Label>
                                    <Select
                                        value={newConnectorForm.type}
                                        onValueChange={(value) => setNewConnectorForm({ ...newConnectorForm, type: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="연동 타입 선택" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                                            {availableConnectorTypes.map((type) => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="enabled"
                                        checked={newConnectorForm.enabled}
                                        onCheckedChange={(checked) => setNewConnectorForm({ ...newConnectorForm, enabled: checked as boolean })}
                                    />
                                    <Label htmlFor="enabled" className="cursor-pointer">추가 시 활성화</Label>
                                </div>
                                <div>
                                    <Label>연동 모드 (선택사항)</Label>
                                    <div className="mt-2 space-y-2">
                                        {['push', 'pull', 'webhook', 'login', 'sync'].map((mode) => (
                                            <div key={mode} className="flex items-center space-x-2">
                                                <Checkbox
                                                    id={`mode-${mode}`}
                                                    checked={newConnectorForm.mode.includes(mode)}
                                                    onCheckedChange={(checked) => {
                                                        if (checked) {
                                                            setNewConnectorForm({
                                                                ...newConnectorForm,
                                                                mode: [...newConnectorForm.mode, mode],
                                                            });
                                                        } else {
                                                            setNewConnectorForm({
                                                                ...newConnectorForm,
                                                                mode: newConnectorForm.mode.filter(m => m !== mode),
                                                            });
                                                        }
                                                    }}
                                                />
                                                <Label htmlFor={`mode-${mode}`} className="cursor-pointer text-sm">
                                                    {mode}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setAddModalOpen(false)}>
                                        취소
                                    </Button>
                                    <Button onClick={handleAddSubmit}>
                                        추가
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* 설정 모달 */}
                    <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>연동 설정</DialogTitle>
                                <DialogDescription>
                                    연동 "{selectedConnector?.connectorId}"의 설정을 수정합니다.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label>Connector ID</Label>
                                    <Input
                                        value={connectorForm.connectorId || ''}
                                        disabled
                                        className="bg-gray-50"
                                    />
                                </div>
                                <div>
                                    <Label>연동 타입</Label>
                                    <Input
                                        value={connectorForm.type || ''}
                                        disabled
                                        className="bg-gray-50"
                                    />
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="settingsEnabled"
                                        checked={connectorForm.enabled || false}
                                        onCheckedChange={(checked) => setConnectorForm({ ...connectorForm, enabled: checked as boolean })}
                                    />
                                    <Label htmlFor="settingsEnabled" className="cursor-pointer">활성화</Label>
                                </div>
                                <div>
                                    <Label>연동 모드</Label>
                                    <div className="mt-2 space-y-2">
                                        {['push', 'pull', 'webhook', 'login', 'sync'].map((mode) => (
                                            <div key={mode} className="flex items-center space-x-2">
                                                <Checkbox
                                                    id={`settings-mode-${mode}`}
                                                    checked={(connectorForm.mode || []).includes(mode)}
                                                    onCheckedChange={(checked) => {
                                                        const currentModes = connectorForm.mode || [];
                                                        if (checked) {
                                                            setConnectorForm({
                                                                ...connectorForm,
                                                                mode: [...currentModes, mode],
                                                            });
                                                        } else {
                                                            setConnectorForm({
                                                                ...connectorForm,
                                                                mode: currentModes.filter(m => m !== mode),
                                                            });
                                                        }
                                                    }}
                                                />
                                                <Label htmlFor={`settings-mode-${mode}`} className="cursor-pointer text-sm">
                                                    {mode}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setSettingsModalOpen(false)}>
                                        취소
                                    </Button>
                                    <Button onClick={handleSettingsSubmit}>
                                        저장
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </>
            ) : (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <p className="text-gray-500">연동 정보를 불러올 수 없습니다.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

IntegrationsPage.displayName = 'IntegrationsPage';
